import { Component, OnInit, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiService } from '../services/api.service';
import { Employee } from '../model/employee.model';
import { Role } from '../model/role';
import { AreaData } from '../model/area.model';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';

@Component({
  selector: 'app-employee-form-print',
  templateUrl: './employee-form-print.component.html',
  styleUrls: ['./employee-form-print.component.scss']
})
export class EmployeeFormPrintComponent implements OnInit, AfterViewInit {
  employee: Employee;
  isNew = true;
  loading = false;
  photoDataUrl: string | null = null;
  employees: Employee[] = [];
  selectedEmployeeId: number | null = null;
  searchValue: string = '';
  documentName: string = '';
  documentFile: File | null = null;
  documentObjectUrl: string | null = null;
  documentPreview: SafeUrl | null = null;
  documentPreviewIsImage: boolean = false;

  // Roles & Areas (master lists + current selections)
  allRoles: Role[] = [];
  allAreas: AreaData[] = [];
  selectedRoleIds: number[] = [];
  selectedAreaCodes: number[] = [];
  
  // Signature properties
  @ViewChild('employeeSignatureCanvas') employeeSignatureCanvas!: ElementRef<HTMLCanvasElement>;
  @ViewChild('hrSignatureCanvas') hrSignatureCanvas!: ElementRef<HTMLCanvasElement>;
  
  private employeeSignatureContext!: CanvasRenderingContext2D;
  private hrSignatureContext!: CanvasRenderingContext2D;
  private isDrawingEmployee = false;
  private isDrawingHr = false;
  private employeeSignatureData: string[] = [];
  private hrSignatureData: string[] = [];
  employeeSignatureDate: string = '';
  hrSignatureDate: string = '';

  // Loaded signature data URLs waiting to be drawn once the canvas is ready
  private pendingEmployeeSignature: string | null = null;
  private pendingHrSignature: string | null = null;

  constructor(
    private apiService: ApiService,
    private route: ActivatedRoute,
    private router: Router,
    private _snackBar: MatSnackBar,
    private sanitizer: DomSanitizer
  ) {}

  ngOnInit(): void {
    const empIdParam = this.route.snapshot.queryParamMap.get('empId');
    this.loadRolesAndAreas();
    this.loadEmployeesList();
    if (empIdParam) {
      this.isNew = false;
      const idNum = Number(empIdParam);
      this.loadEmployee(idNum);
    } else {
      this.isNew = true;
      this.initNewEmployee();
    }
  }

  private loadRolesAndAreas(): void {
    this.apiService.getRolesDetails().subscribe((res: any) => {
      this.allRoles = (res && res.roles) ? res.roles : [];
    });
    this.apiService.getAreaDetails().subscribe((res: any) => {
      this.allAreas = (res && res.areas) ? res.areas : [];
    });
  }

  ngAfterViewInit(): void {
    // Initialize canvases after view is ready
    setTimeout(() => {
      this.initializeSignatureCanvases();
    }, 500); // Increased timeout for better initialization
  }

  private initializeSignatureCanvases(): void {
    console.log('Attempting to initialize signature canvases');
    
    // Check if ViewChild elements are available
    if (!this.employeeSignatureCanvas || !this.hrSignatureCanvas) {
      console.error('ViewChild elements not available yet');
      return;
    }
    
    console.log('Canvas elements found:', {
      employeeCanvas: this.employeeSignatureCanvas.nativeElement,
      hrCanvas: this.hrSignatureCanvas.nativeElement
    });
    
    // Initialize employee signature canvas
    const employeeCanvas = this.employeeSignatureCanvas.nativeElement;
    const employeeContext = employeeCanvas.getContext('2d');
    
    // Initialize HR signature canvas
    const hrCanvas = this.hrSignatureCanvas.nativeElement;
    const hrContext = hrCanvas.getContext('2d');
    
    if (employeeContext && hrContext) {
      // Store contexts
      this.employeeSignatureContext = employeeContext;
      this.hrSignatureContext = hrContext;
      
      // Set up contexts
      this.setupCanvasContext(employeeContext);
      this.setupCanvasContext(hrContext);
      
      // Set canvas dimensions
      this.setCanvasSize(employeeCanvas, employeeContext);
      this.setCanvasSize(hrCanvas, hrContext);
      
      console.log('Signature canvases initialized successfully');

      // Draw any signatures that were loaded before the canvas was ready.
      this.renderPendingSignatures();
    } else {
      console.error('Failed to get canvas contexts', {
        employeeContext: !!employeeContext,
        hrContext: !!hrContext
      });
    }
  }
  
  private setupCanvasContext(context: CanvasRenderingContext2D): void {
    context.strokeStyle = '#000';
    context.lineWidth = 2;
    context.lineCap = 'round';
    context.lineJoin = 'round';
  }

  private setCanvasSize(canvas: HTMLCanvasElement, context: CanvasRenderingContext2D): void {
    // Set explicit canvas size
    canvas.width = 300;
    canvas.height = 80;
    
    // Reset context properties after resizing
    context.strokeStyle = '#000';
    context.lineWidth = 2;
    context.lineCap = 'round';
    context.lineJoin = 'round';
    
    console.log(`Canvas size set to ${canvas.width}x${canvas.height}`);
  }

  startDrawing(event: MouseEvent | TouchEvent, type: 'employee' | 'hr'): void {
    console.log('startDrawing called for:', type, 'Event type:', event.type);
    
    event.preventDefault();
    event.stopPropagation();
    
    // Get canvas and context fresh each time
    const canvas = type === 'employee' ? this.employeeSignatureCanvas?.nativeElement : this.hrSignatureCanvas?.nativeElement;
    let context = type === 'employee' ? this.employeeSignatureContext : this.hrSignatureContext;
    
    console.log('Canvas and context check:', {
      canvas: !!canvas,
      context: !!context,
      type
    });
    
    if (!canvas) {
      console.error('Canvas not found for drawing');
      return;
    }
    
    // If context is not available, try to get it fresh
    if (!context) {
      console.log('Context not available, getting fresh context');
      context = canvas.getContext('2d');
      if (context) {
        // Store the context and set it up
        if (type === 'employee') {
          this.employeeSignatureContext = context;
        } else {
          this.hrSignatureContext = context;
        }
        this.setupCanvasContext(context);
        console.log('Fresh context created and stored');
      } else {
        console.error('Failed to get canvas context');
        return;
      }
    }
    
    // Set drawing flag for this specific canvas
    if (type === 'employee') {
      this.isDrawingEmployee = true;
    } else {
      this.isDrawingHr = true;
    }
    
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    let x, y;
    
    try {
      if (event instanceof MouseEvent) {
        x = (event.clientX - rect.left) * scaleX;
        y = (event.clientY - rect.top) * scaleY;
      } else if (event.touches && event.touches.length > 0) {
        x = (event.touches[0].clientX - rect.left) * scaleX;
        y = (event.touches[0].clientY - rect.top) * scaleY;
      } else {
        console.error('Invalid event type');
        return;
      }
      
      console.log(`Drawing coordinates: (${x}, ${y}) for ${type}`);
      
      context.beginPath();
      context.moveTo(x, y);
      
      // Save current state for undo
      this.saveSignatureState(type, canvas);
      
    } catch (error) {
      console.error('Error in startDrawing:', error);
      if (type === 'employee') {
        this.isDrawingEmployee = false;
      } else {
        this.isDrawingHr = false;
      }
    }
  }
  
  private saveSignatureState(type: 'employee' | 'hr', canvas: HTMLCanvasElement): void {
    try {
      const imageData = canvas.toDataURL();
      if (type === 'employee') {
        this.employeeSignatureData.push(imageData);
      } else {
        this.hrSignatureData.push(imageData);
      }
    } catch (error) {
      console.error('Error saving signature state:', error);
    }
  }

  draw(event: MouseEvent | TouchEvent, type: 'employee' | 'hr'): void {
    const isDrawing = type === 'employee' ? this.isDrawingEmployee : this.isDrawingHr;
    if (!isDrawing) {
      console.log('Not currently drawing');
      return;
    }
    
    event.preventDefault();
    
    const canvas = type === 'employee' ? this.employeeSignatureCanvas?.nativeElement : this.hrSignatureCanvas?.nativeElement;
    let context = type === 'employee' ? this.employeeSignatureContext : this.hrSignatureContext;
    
    if (!canvas) {
      console.error('Canvas not found for drawing');
      return;
    }
    
    // Get fresh context if not available
    if (!context) {
      context = canvas.getContext('2d');
      if (context) {
        if (type === 'employee') {
          this.employeeSignatureContext = context;
        } else {
          this.hrSignatureContext = context;
        }
        this.setupCanvasContext(context);
      } else {
        console.error('Failed to get canvas context in draw');
        return;
      }
    }
    
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    let x, y;
    
    try {
      if (event instanceof MouseEvent) {
        x = (event.clientX - rect.left) * scaleX;
        y = (event.clientY - rect.top) * scaleY;
      } else if (event.touches && event.touches.length > 0) {
        x = (event.touches[0].clientX - rect.left) * scaleX;
        y = (event.touches[0].clientY - rect.top) * scaleY;
      } else {
        return;
      }
      
      context.lineTo(x, y);
      context.stroke();
      
    } catch (error) {
      console.error('Error in draw:', error);
    }
  }

  stopDrawing(type: 'employee' | 'hr'): void {
    if (type === 'employee' && this.isDrawingEmployee) {
      this.isDrawingEmployee = false;
      console.log('Stopped drawing for employee');
    } else if (type === 'hr' && this.isDrawingHr) {
      this.isDrawingHr = false;
      console.log('Stopped drawing for hr');
    }
  }

  testSignature(type: 'employee' | 'hr'): void {
    console.log(`Testing signature for ${type}`);
    console.log('Canvas elements:', {
      employeeCanvas: this.employeeSignatureCanvas,
      hrCanvas: this.hrSignatureCanvas,
      employeeContext: this.employeeSignatureContext,
      hrContext: this.hrSignatureContext
    });
    
    // Try to reinitialize if not available
    if (!this.employeeSignatureCanvas || !this.hrSignatureCanvas || !this.employeeSignatureContext || !this.hrSignatureContext) {
      console.log('Canvas elements not initialized, attempting reinitialization');
      this.initializeSignatureCanvases();
      
      // Wait a bit and try again
      setTimeout(() => {
        this.testSignature(type);
      }, 100);
      return;
    }
    
    const canvas = type === 'employee' ? this.employeeSignatureCanvas.nativeElement : this.hrSignatureCanvas.nativeElement;
    const context = type === 'employee' ? this.employeeSignatureContext : this.hrSignatureContext;
    
    if (!canvas || !context) {
      console.error('Canvas or context still not found for test');
      return;
    }
    
    try {
      // Draw a test line
      context.beginPath();
      context.moveTo(10, 40);
      context.lineTo(100, 40);
      context.lineTo(150, 20);
      context.lineTo(200, 60);
      context.lineTo(290, 40);
      context.stroke();
      
      console.log('Test signature drawn successfully');
    } catch (error) {
      console.error('Error drawing test signature:', error);
    }
  }

  clearSignature(type: 'employee' | 'hr'): void {
    const canvas = type === 'employee' ? this.employeeSignatureCanvas.nativeElement : this.hrSignatureCanvas.nativeElement;
    const context = type === 'employee' ? this.employeeSignatureContext : this.hrSignatureContext;
    
    context.clearRect(0, 0, canvas.width, canvas.height);
    
    if (type === 'employee') {
      this.employeeSignatureData = [];
      this.pendingEmployeeSignature = null;
    } else {
      this.hrSignatureData = [];
      this.pendingHrSignature = null;
    }
  }

  undoSignature(type: 'employee' | 'hr'): void {
    const canvas = type === 'employee' ? this.employeeSignatureCanvas.nativeElement : this.hrSignatureCanvas.nativeElement;
    const context = type === 'employee' ? this.employeeSignatureContext : this.hrSignatureContext;
    const signatureData = type === 'employee' ? this.employeeSignatureData : this.hrSignatureData;
    
    if (signatureData.length > 0) {
      signatureData.pop();
      context.clearRect(0, 0, canvas.width, canvas.height);
      
      if (signatureData.length > 0) {
        const img = new Image();
        img.onload = () => {
          context.drawImage(img, 0, 0);
        };
        img.src = signatureData[signatureData.length - 1];
      }
    }
  }

  getSignatureData(type: 'employee' | 'hr'): string {
    const canvas = type === 'employee' ? this.employeeSignatureCanvas.nativeElement : this.hrSignatureCanvas.nativeElement;
    return canvas.toDataURL();
  }

  isFormValid(): boolean {
    if (!this.employee) return false;
    
    // Check required fields
    if (!this.employee.employeeName || this.employee.employeeName.trim() === '') return false;
    if (!this.employee.employeePhone) return false;
    if (!this.employee.employeeStatus) return false;
    if (this.isNew && (!this.employee.employeePassword || this.employee.employeePassword.trim() === '')) return false;
    
    return true;
  }

  save(): void {
    if (!this.employee || !this.isFormValid()) {
      this._snackBar.open('Please fill all required fields', 'Close', {
        duration: 3000,
      });
      return;
    }

    this.loading = true;
    
    if (this.isNew) {
      const today = new Date();
      const yyyy = today.getFullYear();
      const mm = String(today.getMonth() + 1).padStart(2, '0');
      const dd = String(today.getDate()).padStart(2, '0');
      this.employee.employeeCreationDate = `${yyyy}-${mm}-${dd}`;

      this.apiService.addEmployee(this.buildEmployeePayload('add_employee')).subscribe(
        (data) => {
          if (data != null) {
            this._snackBar.open('Employee data added successfully', 'Close', {
              duration: 3000,
            });
            this.isNew = false;
            this.loadEmployeesList();
          } else {
            this._snackBar.open('Error occurred while adding details', 'Close', {
              duration: 3000,
            });
          }
          this.loading = false;
        },
        (error) => {
          this._snackBar.open('Error occurred while adding details', 'Close', {
            duration: 3000,
          });
          this.loading = false;
        }
      );
    } else {
      this.apiService.updateEmployee(this.buildEmployeePayload('update')).subscribe(
        (data) => {
          if (data.isSuccess === true) {
            this._snackBar.open('Employee data updated successfully', 'Close', {
              duration: 3000,
            });
          } else {
            this._snackBar.open('Error occurred while updating details', 'Close', {
              duration: 3000,
            });
          }
          this.loading = false;
        },
        (error) => {
          this._snackBar.open('Error occurred while updating details', 'Close', {
            duration: 3000,
          });
          this.loading = false;
        }
      );
    }
  }

  private buildEmployeePayload(employeeAction: 'add_employee' | 'update'): Employee {
    const payload: any = {
      areaCode: this.toNullableNumber(this.employee.areaCode),
      employeeId: this.toNullableNumber(this.employee.employeeId),
      employeeName: this.toNullableString(this.employee.employeeName),
      employeePhone: this.toNullableNumber(this.employee.employeePhone),
      employeeEmail: this.toNullableString(this.employee.employeeEmail),
      employeeRole: this.toNullableString(this.employee.employeeRole),
      employeeStatus: this.toNullableString(this.employee.employeeStatus),
      employeeAreaId: this.toNullableString(this.employee.employeeAreaId),
      employeePassword: this.toNullableString(this.employee.employeePassword),
      employeeCreationDate: this.toNullableString(this.employee.employeeCreationDate),
      employeeAction,
      employeeDepartment: this.toNullableString(this.employee.employeeDepartment),
      roles: this.buildSelectedRoles(),
      areas: this.buildSelectedAreas(),
      jobTitle: this.toNullableString(this.employee.jobTitle),
      empAddress: this.toNullableString(this.employee.empAddress),
      educationLevel: this.toNullableString(this.employee.educationLevel),
      startDate: this.toNullableString(this.employee.startDate),
      emergencyName: this.toNullableString(this.employee.emergencyName),
      emergencyPhone: this.toNullableString(this.employee.emergencyPhone),
      emergencyAddress: this.toNullableString(this.employee.emergencyAddress),
      emergencyRelation: this.toNullableString(this.employee.emergencyRelation),
      guarantorName: this.toNullableString(this.employee.guarantorName),
      guarantorPhone: this.toNullableString(this.employee.guarantorPhone),
      guarantorAddress: this.toNullableString(this.employee.guarantorAddress),
      referenceName: this.toNullableString(this.employee.referenceName),
      referencePhone: this.toNullableString(this.employee.referencePhone),
      employeePhoto: this.employee.employeePhoto || null,
      baseSalary: this.toNullableNumber(this.employee.baseSalary),
      bankName: this.toNullableString(this.employee.bankName),
      bankAccount: this.toNullableString(this.employee.bankAccount),
      bankAmount: this.toNullableNumber(this.employee.bankAmount),
      employeeGrade: this.toNullableString(this.employee.employeeGrade),
      taxId: this.toNullableString(this.employee.taxId),
      guarantor: this.toNullableString(this.employee.guarantor),
      documentPath: this.toNullableString(this.employee.documentPath),
      documentName: this.toNullableString(this.employee.documentName),
      documentType: this.toNullableString(this.employee.documentType),
      documentSize: this.toNullableNumber(this.employee.documentSize),
      qiyaas: this.toNullableString(this.employee.qiyaas),
      employeeSignature: this.getSignatureData('employee'),
      hrSignature: this.getSignatureData('hr'),
      employeeSignatureDate: this.toNullableString(this.employeeSignatureDate),
      hrSignatureDate: this.toNullableString(this.hrSignatureDate)
    };

    return payload as Employee;
  }

  private buildSelectedRoles(): Role[] {
    return (this.selectedRoleIds || []).map(id => {
      const match = this.allRoles.find(r => Number(r.roleId) === Number(id));
      return match ? match : ({ roleId: id } as Role);
    });
  }

  private buildSelectedAreas(): AreaData[] {
    return (this.selectedAreaCodes || []).map(code => {
      const match = this.allAreas.find(a => Number(a.areaCode) === Number(code));
      return match ? match : ({ areaCode: code } as AreaData);
    });
  }

  private toNullableString(value: any): string | null {
    if (value === null || value === undefined) {
      return null;
    }

    const text = String(value).trim();
    return text === '' ? null : text;
  }

  private toNullableNumber(value: any): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const numberValue = Number(value);
    return isNaN(numberValue) ? null : numberValue;
  }

  print(): void {
    window.print();
  }

  // ... (rest of the existing methods remain the same)
  onSelectEmployeeChange(value: string): void {
    if (!value || value === 'new') {
      this.isNew = true;
      this.initNewEmployee();
      return;
    }
    const id = Number(value);
    if (!isNaN(id)) {
      this.isNew = false;
      this.selectedEmployeeId = id;
      this.loadEmployee(id);
    }
  }

  searchEmployee(): void {
    if (!this.searchValue || this.searchValue.trim() === '') {
      return;
    }

    console.log('Searching for employee:', this.searchValue); // Debug log

    const searchLower = this.searchValue.toLowerCase().trim();
    const foundEmployee = this.employees.find(emp => 
      emp.employeeId.toString() === searchLower || 
      emp.employeeName.toLowerCase().includes(searchLower)
    );

    if (foundEmployee) {
      console.log('Found employee:', foundEmployee); // Debug log
      this.isNew = false;
      this.selectedEmployeeId = foundEmployee.employeeId;
      
      // Load full employee details including photo and other fields
      this.loadEmployee(foundEmployee.employeeId);
      
      this._snackBar.open('Employee found and loaded for editing', 'Close', {
        duration: 3000,
      });
    } else {
      console.log('Employee not found in list'); // Debug log
      this._snackBar.open('Employee not found', 'Close', {
        duration: 3000,
      });
    }
  }

  private initNewEmployee(): void {
    this.loading = true;
    this.apiService.getEmplyees().subscribe(res => {
      const list: Employee[] = res.employees || [];
      const maxId = list.length ? Math.max(...list.map(e => e.employeeId || 0)) : 0;
      this.employee = {
        areaCode: null,
        employeeId: maxId + 1,
        employeeName: '',
        employeePhone: null,
        employeeEmail: '',
        employeeRole: '',
        employeeStatus: 'Y',
        employeeAreaId: '',
        employeePassword: '',
        employeeCreationDate: '',
        employeeAction: 'add_employee',
        employeeDepartment: '',
        roles: [],
        areas: [],
        jobTitle: '',
        empAddress: '',
        educationLevel: '',
        startDate: '',
        emergencyName: '',
        emergencyPhone: '',
        emergencyAddress: '',
        emergencyRelation: '',
        guarantorName: '',
        guarantorPhone: '',
        guarantorAddress: '',
        referenceName: '',
        referencePhone: '',
        baseSalary: null,
        bankName: '',
        bankAccount: '',
        bankAmount: null,
        employeeGrade: '',
        taxId: '',
        guarantor: '',
        // Document fields
        documentPath: '',
        documentName: '',
        documentType: '',
        documentSize: null,
        qiyaas: null,
        employeePhoto: null,
        employeeSignature: null,
        hrSignature: null,
        employeeSignatureDate: null,
        hrSignatureDate: null
      } as Employee;
      this.photoDataUrl = null;
      this.employeeSignatureDate = '';
      this.hrSignatureDate = '';
      this.selectedRoleIds = [];
      this.selectedAreaCodes = [];
      this.pendingEmployeeSignature = null;
      this.pendingHrSignature = null;
      this.loading = false;
    }, _ => {
      this.loading = false;
    });
  }

  private loadEmployeesList(): void {
    this.apiService.getEmplyees().subscribe(res => {
      this.employees = res && res.employees ? res.employees : [];
    });
  }

  private loadEmployee(empId: number): void {
    this.loading = true;
    this.apiService.getEmplyeeDetailsById(empId).subscribe(res => {
      const data: any = res || {};
      console.log('Loaded employee data:', data); // Debug log
      this.selectedEmployeeId = Number(data.employeeId ?? empId);
      
      this.employee = {
        areaCode: Number(data.areaCode ?? 0),
        employeeId: Number(data.employeeId ?? 0),
        employeeName: String(data.employeeName ?? ''),
        employeePhone: Number(data.employeePhone ?? 0),
        employeeEmail: String(data.employeeEmail ?? ''),
        employeeRole: data.employeeRole,
        employeeStatus: String(data.employeeStatus ?? ''),
        employeeAreaId: '',
        employeePassword: String(data.employeePassword ?? ''),
        employeeCreationDate: String(data.employeeCreationDate ?? ''),
        employeeAction: 'update',
        employeeDepartment: data.employeeDepartment,
        roles: data.roles || [],
        areas: data.areas || [],
        jobTitle: data.jobTitle,
        empAddress: data.empAddress,
        educationLevel: data.educationLevel,
        startDate: data.startDate,
        // Emergency Contact fields - check multiple possible field names
        emergencyName: data.emergencyName || data.emergency_contact_name || '',
        emergencyPhone: data.emergencyPhone || data.emergency_contact_phone || '',
        emergencyAddress: data.emergencyAddress || data.emergency_contact_address || '',
        emergencyRelation: data.emergencyRelation || data.emergency_contact_relation || '',
        // Guarantor fields - check multiple possible field names
        guarantorName: data.guarantorName || data.guarantor_name || '',
        guarantorPhone: data.guarantorPhone || data.guarantor_phone || '',
        guarantorAddress: data.guarantorAddress || data.guarantor_address || '',
        guarantor: data.guarantor || data.guarantor_type || '',
        // Reference fields - check multiple possible field names
        referenceName: data.referenceName || data.reference_name || '',
        referencePhone: data.referencePhone || data.reference_phone || '',
        employeePhoto: data.employeePhoto || data.photo_path || null,
        baseSalary: data.baseSalary,
        bankName: data.bankName,
        bankAccount: data.bankAccount,
        bankAmount: data.bankAmount,
        employeeGrade: data.employeeGrade,
        taxId: data.taxId,
        // Document fields
        documentPath: data.documentPath || '',
        documentName: data.documentName || '',
        documentType: data.documentType || '',
        documentSize: data.documentSize || null,
        qiyaas: data.qiyaas || null,
        employeeSignature: data.employeeSignature || null,
        hrSignature: data.hrSignature || null,
        employeeSignatureDate: data.employeeSignatureDate || null,
        hrSignatureDate: data.hrSignatureDate || null
      } as Employee;

      // Prepopulate multi-select role/area selections from loaded employee
      this.selectedRoleIds = (data.roles || [])
        .map((r: any) => r && r.roleId != null ? Number(r.roleId) : null)
        .filter((id: number | null) => id != null) as number[];
      this.selectedAreaCodes = (data.areas || [])
        .map((a: any) => a && a.areaCode != null ? Number(a.areaCode) : null)
        .filter((code: number | null) => code != null) as number[];

      console.log('Employee object after mapping:', this.employee); // Debug log

      // Enhanced photo loading logic
      if (data.employeePhoto) {
        console.log('Loading employee photo:', data.employeePhoto); // Debug log
        this.photoDataUrl = 'data:image/jpeg;base64,' + data.employeePhoto;
      } else if (data.photo_path) {
        console.log('Loading employee photo from photo_path:', data.photo_path); // Debug log
        this.photoDataUrl = 'data:image/jpeg;base64,' + data.photo_path;
      } else {
        console.log('No photo found for employee'); // Debug log
        this.photoDataUrl = null;
      }
      
      // Store existing signatures and render them once the canvas is ready.
      this.pendingEmployeeSignature = data.employeeSignature || null;
      this.pendingHrSignature = data.hrSignature || null;
      this.renderPendingSignatures();

      // Reconstruct the document preview from the saved data URL (if any).
      this.documentObjectUrl = null;
      this.documentName = data.documentName || '';
      const savedDoc = data.documentPath || '';
      this.documentPreviewIsImage = savedDoc.startsWith('data:image/');
      this.documentPreview = this.documentPreviewIsImage
        ? this.sanitizer.bypassSecurityTrustUrl(savedDoc)
        : null;
      if (data.employeeSignatureDate) {
        this.employeeSignatureDate = data.employeeSignatureDate;
      } else {
        this.employeeSignatureDate = '';
      }
      if (data.hrSignatureDate) {
        this.hrSignatureDate = data.hrSignatureDate;
      } else {
        this.hrSignatureDate = '';
      }
      
      this.loading = false;
    }, _ => {
      this.loading = false;
    });
  }

  private renderPendingSignatures(): void {
    if (this.pendingEmployeeSignature) {
      this.loadSignature(this.pendingEmployeeSignature, 'employee');
    }
    if (this.pendingHrSignature) {
      this.loadSignature(this.pendingHrSignature, 'hr');
    }
  }

  private loadSignature(signatureData: string, type: 'employee' | 'hr'): void {
    const canvasRef = type === 'employee' ? this.employeeSignatureCanvas : this.hrSignatureCanvas;
    const canvas = canvasRef ? canvasRef.nativeElement : null;
    if (!canvas || !signatureData) {
      // Canvas not in the DOM yet; keep the data pending so it can be drawn
      // once the canvas is initialized (see initializeSignatureCanvases()).
      return;
    }

    // Ensure we have a usable context even if init has not stored one yet.
    let context = type === 'employee' ? this.employeeSignatureContext : this.hrSignatureContext;
    if (!context) {
      const fresh = canvas.getContext('2d');
      if (!fresh) {
        return;
      }
      context = fresh;
      this.setupCanvasContext(context);
      if (type === 'employee') {
        this.employeeSignatureContext = context;
      } else {
        this.hrSignatureContext = context;
      }
    }

    const ctx = context;
    const img = new Image();
    img.onload = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    };
    img.src = signatureData;
  }

  onPhotoSelected(event: any): void {
    const file = event.target && (event.target as HTMLInputElement).files && (event.target as HTMLInputElement).files[0];
    if (!file) {
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      this.photoDataUrl = result;
      const base64 = result.split(',')[1] || '';
      (this.employee as any).employeePhoto = base64;
    };
    reader.readAsDataURL(file);
  }

  onDocumentSelected(event: any): void {
    const file = event.target.files[0];
    if (file) {
      this.documentFile = file;
      this.documentName = file.name;
      this.employee.documentName = file.name;
      this.employee.documentType = file.type;
      this.employee.documentSize = file.size;

      // Build a preview/full-view URL for the freshly selected file.
      if (this.documentObjectUrl) {
        URL.revokeObjectURL(this.documentObjectUrl);
      }
      this.documentObjectUrl = URL.createObjectURL(file);
      this.documentPreviewIsImage = (file.type || '').startsWith('image/');
      this.documentPreview = this.documentPreviewIsImage
        ? this.sanitizer.bypassSecurityTrustUrl(this.documentObjectUrl)
        : null;

      // Read the file content into documentPath as a base64 data URL so it is
      // persisted to the DB (document_path is now LONGTEXT).
      const reader = new FileReader();
      reader.onload = () => {
        this.employee.documentPath = reader.result as string;
      };
      reader.readAsDataURL(file);
    }
  }

  removePhoto(): void {
    this.photoDataUrl = null;
    (this.employee as any).employeePhoto = null;
  }

  removeDocument(): void {
    if (this.documentObjectUrl) {
      URL.revokeObjectURL(this.documentObjectUrl);
    }
    this.documentObjectUrl = null;
    this.documentPreview = null;
    this.documentPreviewIsImage = false;
    this.documentFile = null;
    this.documentName = '';
    this.employee.documentName = '';
    this.employee.documentType = '';
    this.employee.documentSize = null;
    this.employee.documentPath = '';
  }

  viewDocument(): void {
    // Prefer the freshly selected file (object URL); fall back to a saved path.
    const url = this.documentObjectUrl || this.employee.documentPath;
    if (!url) {
      return;
    }
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}
